import { useSyncExternalStore } from 'react'

/**
 * Shared, runtime-tweakable colour mix for the mesh hero background.
 *
 * The four stops match what `MeshHeroBackground` shipped hardcoded, so the
 * default render is byte-identical to before. The `?bg_color_picker=true` tool
 * (`MeshColorPicker`) lets anyone live-tweak these to explore palettes without
 * touching code; it never changes the defaults, so the site looks the same for
 * everyone else.
 */
export type MeshColors = readonly [string, string, string, string]

/** Light field, warm glow, cloud, light field. */
export const DEFAULT_MESH_COLORS: MeshColors = ['#ffffff', '#ffaf6e', '#cddcf9', '#ffffff']

export const MESH_COLOR_LABELS: readonly [string, string, string, string] = [
  'Light 1',
  'Warm',
  'Cloud',
  'Light 2',
]

let current: MeshColors = DEFAULT_MESH_COLORS
const listeners = new Set<() => void>()

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function getSnapshot(): MeshColors {
  return current
}

/** SSR + first client render always start from the defaults, so hydration matches. */
function getServerSnapshot(): MeshColors {
  return DEFAULT_MESH_COLORS
}

export function setMeshColors(next: MeshColors): void {
  current = next
  for (const listener of listeners) listener()
}

export function setMeshColorAt(index: number, value: string): void {
  const next = [...current] as [string, string, string, string]
  next[index] = value
  setMeshColors(next)
}

export function resetMeshColors(): void {
  setMeshColors(DEFAULT_MESH_COLORS)
}

export function useMeshColors(): MeshColors {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}
